import React, { useState, useMemo } from "react"; // ✅ Added useMemo
import Sidebar from "./Sidebar";
import ProductCard from "./ProductCard";
import RecentlyViewed from "./RecentlyViewed";
import products from "../common/data/products";
import Banner1 from "../../assets/images/shopbanner1.webp";
import { Menu, LayoutGrid, List, ChevronDown } from "lucide-react";

const ShopPage = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16); // ✅ Added state for items per page
  const [sortType, setSortType] = useState("latest"); // ✅ Added state for sort type

  // ✅ Sorting logic
  const sortedProducts = useMemo(() => {
    const toNumber = (price) => parseFloat(price.replace(/[^0-9.]/g, "")) || 0;
    let sorted = [...products];
    if (sortType === "popularity") {
      sorted.sort((a, b) => b.rating - a.rating); // assuming rating exists
    } else if (sortType === "price") {
      sorted.sort((a, b) => toNumber(a.price) - toNumber(b.price));
    }
    return sorted;
  }, [sortType]);

  // ✅ Pagination logic
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const paginatedProducts = useMemo(() => {
    const start = (activePage - 1) * itemsPerPage;
    return sortedProducts.slice(start, start + itemsPerPage);
  }, [activePage, itemsPerPage, sortedProducts]);

  return (
    <>
      <div className="bg-white mt-12">
        <div className="max-w-screen-xl mx-auto flex flex-col lg:flex-row gap-6 px-4 sm:px-6 lg:px-8 py-4 relative">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-[260px] shrink-0">
            <Sidebar show={true} setShow={() => {}} />
          </div>

          {/* ✅ Mobile Sidebar - only visible on small screens */}
          <div className="block lg:hidden">
            <Sidebar show={showSidebar} setShow={setShowSidebar} />
          </div>

          <div className="flex-1">
            {/* Banner */}
            <div className="relative w-full mb-8">
              <img
                src={Banner1}
                alt="Banner"
                className="w-full object-cover h-64 sm:h-80 md:h-[375px]"
              />
              <div className="absolute top-0 left-0 w-full h-full flex flex-col px-6 md:px-12 text-black">
                <h1 className="text-xl sm:text-2xl md:text-4xl font-bold mb-1 leading-tight mt-24 ml-4 sm:ml-8">
                  Plus-Size Styles for
                </h1>
                <h2 className="text-xl sm:text-2xl md:text-4xl italic tracking-wide mb-3 ml-4 sm:ml-8">
                  Every Season
                </h2>
                <p className="text-xs sm:text-sm md:text-base text-gray-800 font-medium max-w-lg ml-4 sm:ml-8">
                  Quis quam suspendisse ultrices gravida. Risus
                  <br />
                  commodo viverra maecenas.
                </p>
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 text-sm gap-4">
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="flex items-center gap-2 text-gray-500">
                  <LayoutGrid className="w-5 h-5 cursor-pointer hover:text-black hidden sm:block" />
                  <List className="w-5 h-5 cursor-pointer hover:text-black hidden sm:block" />
                  <Menu
                    className="w-6 h-6 text-black cursor-pointer block sm:hidden"
                    onClick={() => setShowSidebar(true)}
                  />
                </div>
                <p className="text-black hidden sm:block">
                  Showing {(activePage - 1) * itemsPerPage + 1}–
                  {Math.min(activePage * itemsPerPage, sortedProducts.length)}{" "}
                  of {sortedProducts.length} results
                </p>
              </div>

              {/* ✅ Updated dropdowns to be functional */}
              <div className="flex items-center gap-4 w-full sm:w-auto justify-end">
                <div className="relative text-gray-400">
                  <label className="mr-1">Show:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setActivePage(1);
                    }}
                    className="text-black font-medium bg-transparent appearance-none pr-6"
                  >
                    <option value={16}>16 Items</option>
                    <option value={24}>24 Items</option>
                    <option value={36}>36 Items</option>
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none text-black" />
                </div>

                <span className="text-gray-300 hidden sm:inline">|</span>

                <div className="relative text-black font-medium">
                  <select
                    value={sortType}
                    onChange={(e) => {
                      setSortType(e.target.value);
                      setActivePage(1);
                    }}
                    className="bg-transparent appearance-none pr-6"
                  >
                    <option value="latest">Sort by latest</option>
                    <option value="popularity">Sort by popularity</option>
                    <option value="price">Sort by price</option>
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* ✅ Dynamic Product List */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {paginatedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {/* ✅ Working Pagination */}
            <div className="flex justify-center items-center gap-2 mb-12">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (num) => (
                  <button
                    key={num}
                    onClick={() => setActivePage(num)}
                    className={`w-8 h-8 rounded border border-black text-sm ${
                      activePage === num
                        ? "bg-black text-white"
                        : "hover:bg-black hover:text-white"
                    }`}
                  >
                    {num}
                  </button>
                )
              )}
              {activePage < totalPages && (
                <button
                  onClick={() => setActivePage(activePage + 1)}
                  className="px-2 text-black hover:underline"
                >
                  →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recently Viewed */}
      <div className="max-w-screen-xl mx-auto px-4 lg:px-5 py-2 bg-white mb-7">
        <RecentlyViewed />
      </div>
    </>
  );
};

export default ShopPage;
